import API from "../Services/API";
import Model from "./Model";
import { toQueryString } from "../Services/helpers";
import State from "./State";
import { useToasts } from "../Components/Toasts";
import makeBatcher from "./Batcher";
import { OfflineQueue } from "../Components/Offline";
import { useSimpleWrapper } from "./SimpleWrapper";

const useCustomers = (searchData, alwaysRefresh) => {
    let searchStr = toQueryString(searchData)

    return Model({
        alwaysRefresh,
        stateKey: ['customers', searchStr],
        fetchData: () => API.get('/customers?perPage=9999&include=jobs&'+searchStr),
        persist: searchStr.length === 0,
        returnDataKey: 'customers',
        saveAdditionalData: (response) => {
            response.map((customer) => {
                State.putIfNotExists(['customer', customer.id], customer);
                return customer;
            });
        },
        defaultLoadingError: 'Failed to load customers.',
    });
}


const customerBatcher = makeBatcher(
    'customer',
    (customerIds) => '/customers?'+toQueryString({customerIds}),
    'id',
    'Failed to load customers.',
    20,
    50,
);

const useCustomer = (customerId, alwaysRefresh, allowBatching = false) => {
    const {showError} = useToasts();

    return Model({
        block: !customerId,
        alwaysRefresh,
        stateKey: ['customer', customerId],
        fetchData: () => {
            if (allowBatching) {
                return customerBatcher(customerId, showError);
            }
            return API.get('/customers/'+customerId);
        },
        returnDataKey: 'customer',
        registerAdditionalMethods: (opts) => {
            return {
                updateCustomer: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/customers/'+customerId, data),
                        null,
                        true,
                    );
                },
                updateCommunicationPreferences: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/communicationPreferences`, data),
                        null,
                        true,
                    );
                },
                changeSecurity: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/changeSecurity`),
                        null,
                        true,
                    );
                },
                addQuote: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/addQuote`, data),
                        null,
                        true,
                    );
                },
                scheduleJob: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/schedule`, data),
                        null,
                        true,
                    );
                },
                unscheduleJob: (jobId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/unschedule`),
                        null,
                        true,
                    );
                },
                markJobAsDone: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/done`, data),
                        'Job marked as done',
                        true,
                        true,
                        {
                            task: OfflineQueue.TASK_MARK_JOB_DONE,
                            entity: {model: 'Customers/Job', id: jobId, customerId: customerId}
                        },
                    );
                },
                skipJob: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/skip`, data),
                        null,
                        true,
                    );
                },
                updateJobOccurrence: (jobId, occurrenceId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/occurrences/${occurrenceId}`, data),
                        null,
                        true,
                    );
                },
                deleteJobOccurrence: (jobId, occurrenceId, deleteInvoice = false) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/jobs/${jobId}/occurrences/${occurrenceId}`, {deleteInvoice}),
                        null,
                        true,
                    );
                },
                updateJob: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs${jobId ? '/'+jobId : ''}`, data),
                        null,
                        true,
                    );
                },
                updateJobItemisationAdjustment: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/adjustment`, data),
                        null,
                        true,
                    );
                },
                addExternalPayment: (data) => {
                    data.customerId = customerId;
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/external`, data),
                        null,
                        true,
                    );
                },
                addLoyaltyCard: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/loyaltyCards`, data),
                        null,
                        true,
                    );
                },
                cancelLoyaltyCard: (id) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/loyaltyCards/${id}`),
                        null,
                        true,
                    );
                },
                editBalance: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/${data.action}`, data),
                        null,
                        true,
                    );
                },
                chargePaymentMethod: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/chargePaymentMethod`, data),
                        null,
                        true,
                    );
                },
                cancelPendingCharge: () => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/pendingCharge`),
                        null,
                        true,
                    );
                },
                cancelJob: (jobId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/cancel`),
                        null,
                        true,
                    );
                },
                resumeJob: (jobId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/resume`),
                        null,
                        true,
                    );
                },
                deleteJob: (jobId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/jobs/${jobId}`),
                        null,
                        true,
                    );
                },
                deleteInvoice: (invoiceId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/invoices/${invoiceId}`),
                        null,
                        true,
                    );
                },
                deleteCustomer: () => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}`),
                        null,
                        true,
                    );
                },
                anonymiseCustomer: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/anonymise`),
                        null,
                        true,
                    );
                },
                purgeCustomer: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/purge`),
                        null,
                        true,
                    );
                },
                restoreCustomer: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/restore`),
                        null,
                        true,
                        false,
                    );
                },
                triggerNotification: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/triggerNotification`, data),
                        "The notification is being sent",
                        true,
                    );
                },
                removeTags: (tagIds) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/tags`, {tagIds}),
                        null,
                        true,
                    );
                },
                removeJobTags: (jobId, tagIds) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/jobs/${jobId}/tags`, {tagIds}),
                        null,
                        true,
                    );
                }
            }
        },
        defaultLoadingError: 'Failed to load customer.',
    });
}

const useReport = (alwaysRefresh = false) => {
    return Model({
        stateKey: 'customer-report',
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/customers/report`),
        returnDataKey: 'report',
    })
}

const useUpdateMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/customers/update', data),
        'Customers were updated',
        'Sorry, there was an error updating the customers',
    )
}

const useDeleteMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/customers/delete', data),
        'Customers were archived',
        'Sorry, there was an error archiving the customers',
    )
}

const useRestoreMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/customers/restore', data),
        'Customers were restored',
        'Sorry, there was an error restoring the customers',
    )
}

const invoiceStatuses = [
    'Outstanding',
    'Overdue',
    'Overdue (2nd reminder)',
    'Paid',
    // 'Deleted',
];

const customerStatuses = {
    ACTIVE: 'Active',
    DELETED: 'Deleted',
    ANONYMISED: 'Anonymised',
    PURGED: 'Purged',
};

export {
    useCustomers,
    useCustomer,
    useReport,
    useUpdateMany,
    useDeleteMany,
    useRestoreMany,
    invoiceStatuses,
    customerStatuses,
};
